// SPDX-License-Identifier: GPL-3.0-or-later
// All the feedback layers of the game page in one object: effects, sound, music, the stream
// behind the table, and the touch haptics. The page makes it once, after the renderer. The game flow
// talks to it through the `FlowFeedback` port. It is never part of the /overlay bundle.
//
// The layers only read the game. A layer that throws is switched off for the rest of the page
// session, so an effect can never stop the game.

import type { PinballRenderer } from "../src/render/index.ts";
import type { RenderFrame } from "../src/shared/contracts.ts";
import type { FeedbackKind } from "../src/shared/feedback/index.ts";
import { bridgeEngineEvents } from "../src/table/feedback/index.ts";
import type { RunSave, RunView } from "../src/table/run/types.ts";
import { type AudioLayer, createAudioLayer, type GestureTarget } from "./audio-layer.ts";
import type { FeedbackCore } from "./feedback-core.ts";
import type { FlowFeedback } from "./game-flow.ts";
import {
  type BestStorage,
  cleanScore,
  createHighScore,
  type HighScore,
  type StageResult,
} from "./high-score.ts";
import { isGameIdle } from "./screens.ts";
import { createStreamLayer, type StreamLayer, type StreamLayerDeps } from "./stream-layer.ts";
import { cleanName } from "./text-cues.ts";
import type { HapticKind } from "./touch-controls.ts";
import { createVisualLayer, type VisualLayer } from "./visual-layer.ts";

/** The moments that give a strong vibration on a touch device. */
export const BIG_MOMENTS: ReadonlySet<FeedbackKind> = new Set<FeedbackKind>([
  "jackpot",
  "multiball_start",
  "mode_start",
]);

/** The part of the touch controls that the layers use. */
export interface TouchPort {
  pulse(kind: HapticKind): void;
  setIdle(idle: boolean): void;
}

/** What the layers take from the page. */
export interface LayersDeps {
  readonly core: FeedbackCore;
  readonly view: PinballRenderer;
  readonly touch: TouchPort;
  /** The document: it gets the audio gestures and the visibility events. */
  readonly doc: GestureTarget;
  readonly createContext: () => AudioContext | null;
  readonly now: () => number;
  /**
   * The stream: the element, the page address and the window for the size. `enabled` is the kill
   * switch and `live` holds the platform of the live check. A page leaves both out.
   */
  readonly stream: Pick<
    StreamLayerDeps,
    "host" | "location" | "win" | "playfield" | "enabled" | "live"
  >;
  /** The storage of the best score, or null. */
  readonly storage: BestStorage | null;
  /** The run now (read only), or null. The layers read it at the end of a game. */
  readonly runView?: () => RunView | null;
  /** Called after each write of the run save, with the save in memory. */
  readonly onSave?: (save: RunSave) => void;
  /** Called once when a layer throws. The layer is then off. Default: nothing. */
  readonly onError?: (layer: string, error: unknown) => void;
}

/** The layers. They are the feedback port of the game flow. */
export interface FeedbackLayers extends FlowFeedback {
  /** Start the audio. Call it from a user gesture. */
  unlock(): void;
  /** A draft holds the table (true) or lets it go on (false). The effects stand still then. */
  hold(held: boolean): void;
  /** The reduced motion choice of the page. */
  setReducedMotion(on: boolean): void;
  /** The audio layer, for a reader that needs the engine. */
  readonly audio: AudioLayer;
  /** The visual layer, for a reader that counts the effects. */
  readonly visual: VisualLayer;
  readonly stream: StreamLayer;
  /** The run save in memory. Read it after a write: it never reads the storage again. */
  save(): RunSave;
  /** Remove every layer. */
  dispose(): void;
}

/**
 * Run `fn`. When it throws, report once and run it never again. This keeps an effect from stopping
 * the game, and keeps a broken layer from filling the console at each frame.
 * The wrapper has three fixed parameters, so a call in the frame loop builds no array. An argument
 * that the caller leaves out reaches `fn` as undefined.
 */
export function guarded<A = undefined, B = undefined, C = undefined>(
  name: string,
  onError: ((layer: string, error: unknown) => void) | undefined,
  fn: (a: A, b: B, c: C) => void,
): (a?: A, b?: B, c?: C) => void {
  let failed = false;
  return (a, b, c) => {
    if (failed) return;
    try {
      fn(a as A, b as B, c as C);
    } catch (error) {
      failed = true;
      onError?.(name, error);
    }
  };
}

/** The 0-based indexes of the stages that ran in a run: from the first stage to the one now. */
function ranIndexes(view: RunView): number[] {
  const out: number[] = [];
  for (let i = view.startIndex; i <= view.stageIndex; i += 1) out.push(i);
  return out;
}

/** The stage records of a game. */
interface StageNotes {
  /** A stage starts: clear its new flag and read its best from the save. */
  start(stage: number): void;
  /** Record the points of a stage. */
  note(stage: number, points: number, cleared: boolean): void;
  /** True when the stage set a new stage best in this game. */
  isNew(index: number): boolean;
}

/** Make the stage records: the bests before this game and the new bests of this game. */
function createStageNotes(best: HighScore, bus: FeedbackCore["bus"]): StageNotes {
  // The stages that set a new stage best in the game that runs. A stage start clears its flag.
  const stageNew: boolean[] = [];
  // The stage best of each stage before this game. A stage start reads it from the save.
  const stageBase: number[] = [];

  /** The stage best before this game. A page that saw no stage start reads it at the first use. */
  const baseOf = (index: number): number => {
    const known = stageBase[index];
    if (known !== undefined) return known;
    const now = best.save().stageBest[index] ?? 0;
    stageBase[index] = now;
    return now;
  };

  return {
    start(stage) {
      stageNew[stage - 1] = false;
      stageBase[stage - 1] = best.save().stageBest[stage - 1] ?? 0;
    },
    /**
     * The points must beat the stage best from before this game, and that best must be above 0. The
     * first record gives no cue. A stage gives `level_best` once in a game: the director keeps
     * adding points to a cleared last stage until the game ends.
     */
    note(stage, points, cleared) {
      const index = stage - 1;
      const base = baseOf(index);
      const clean = cleanScore(points);
      const isNew = clean > base && base > 0;
      best.recordStage(index, clean, cleared);
      if (!isNew || stageNew[index] === true) return;
      stageNew[index] = true;
      bus.emit({ kind: "level_best", stage, score: clean, strength: 1 });
    },
    isNew: (index) => stageNew[index] === true,
  };
}

/** The stage tracking of a game. */
interface StageTracker {
  /** Listen for stage starts and clears. It returns the function that stops the listening. */
  subscribe(): () => void;
  /** Record the stage that runs now and list the stages that ran. No run: an empty list. */
  settle(): StageResult[];
}

/** Make the stage tracking. */
function createStageTracker(
  best: HighScore,
  bus: FeedbackCore["bus"],
  runView: (() => RunView | null) | undefined,
): StageTracker {
  const notes = createStageNotes(best, bus);
  return {
    // A stage clear unlocks the next stage at once. The clear of stage 5 counts once per run here.
    subscribe: () =>
      bus.subscribe((ev) => {
        if (ev.kind === "stage_start") notes.start(ev.stage);
        else if (ev.kind === "stage_clear") notes.note(ev.stage, ev.scoreDelta ?? 0, true);
      }),
    settle() {
      const view = runView?.() ?? null;
      if (view === null || view.phase === "idle") return [];
      notes.note(view.stageIndex + 1, view.stagePoints[view.stageIndex] ?? 0, false);
      const save = best.save();
      return ranIndexes(view).map((index) => ({
        index,
        points: view.stagePoints[index] ?? 0,
        best: save.stageBest[index] ?? 0,
        isNew: notes.isNew(index),
      }));
    },
  };
}

/** Make the layers. */
export function createFeedbackLayers(deps: LayersDeps): FeedbackLayers {
  const { core, touch, onError } = deps;
  const best = createHighScore(deps.storage, deps.onSave);
  const tracker = createStageTracker(best, core.bus, deps.runView);
  const audio = createAudioLayer({
    doc: deps.doc,
    bus: core.bus,
    settings: core.store,
    createContext: deps.createContext,
  });
  const visual: VisualLayer = createVisualLayer({
    view: deps.view,
    bus: core.bus,
    settings: core.store,
    limiter: core.limiter,
    now: deps.now,
  });
  const stream = createStreamLayer({ ...deps.stream, view: deps.view, settings: core.store });
  const stopPulse = core.bus.subscribe((ev) => {
    if (BIG_MOMENTS.has(ev.kind)) touch.pulse("big");
  });
  const stopRun = tracker.subscribe();
  let idle: boolean | null = null;
  // True while a perk draft holds the table. The effects stand still then.
  let held = false;

  // The effects and the charge tone have their own guards: a broken effect must not stop the tone.
  const runEffects = guarded("effects", onError, (nowMs: number, dtMs: number, f: RenderFrame) =>
    visual.frame(nowMs, dtMs, f),
  );
  const runCharge = guarded("charge", onError, (f: RenderFrame) => core.chargeFrame(f));
  const tickAudio = guarded("music", onError, audio.tick);

  return {
    audio,
    visual,
    stream,
    unlock: audio.unlock,
    save: () => best.save(),
    setReducedMotion: (on) => visual.setReducedMotion(on),
    bridge: (engine) => bridgeEngineEvents(engine, core.bus, core.positionOf),
    frame(nowMs, dtMs, f) {
      // A held draft stops the effects. The plunger charge tone still follows the frame.
      if (!held) runEffects(nowMs, dtMs, f);
      runCharge(f);
    },
    hold(on) {
      held = on;
      audio.setHeld(on);
    },
    tick(nowMs, game) {
      tickAudio(nowMs, game);
      const nowIdle = isGameIdle(game.phase);
      if (nowIdle !== idle) {
        idle = nowIdle;
        touch.setIdle(nowIdle);
      }
    },
    pause: (paused) => audio.setPaused(paused),
    gameEnd(finalScore, ended) {
      // The engine reports a game over by itself. An ended game has none: send one, so the lamps go
      // back to attract and the music and the sound end the game.
      if (ended) core.bus.emit({ kind: "game_over", finalScore, strength: 1 });
      const stages = tracker.settle();
      const result = best.record(finalScore);
      if (result.isNew) core.bus.emit({ kind: "high_score", score: result.best, strength: 1 });
      return stages.length > 0 ? { ...result, stages } : result;
    },
    turnStart(name) {
      const label = cleanName(name);
      core.bus.emit(
        label === ""
          ? { kind: "featured_turn_start", strength: 1 }
          : { kind: "featured_turn_start", label, strength: 1 },
      );
    },
    dispose() {
      stopPulse();
      stopRun();
      audio.dispose();
      visual.dispose();
      stream.dispose();
    },
  };
}
